// Pure per-tick step of the fell-behind alert (StreamRift #178). Extracted from
// the PositionTracker effect so reconnect, recovery, and duplicate suppression
// are focused-testable — same discipline as stepDivergenceLatch
// (lib/divergence/polyKalshiDivergence.ts).
//
// Rules:
//  - NEW-LEVEL cue: queueAbove 0 -> >0 fires once; re-arms only when the order
//    returns to exactly 0 (positive churn never re-fires).
//  - WAY-BEHIND cue: crossing up through 200k fires once; re-arms by dropping
//    back below 200k (pre-existing behavior, preserved).
//  - One update crossing both boundaries yields ONE firedOrderIds entry.
//  - First sight (no prior sample: new order, page load, reconnect) seeds
//    silently, even if already behind.
//  - queueAbove null drops the sample, so the next non-null reading is a silent
//    first sight (matches the previous inline behavior).
//  - Orders absent from metricsByOrderId are pruned (nextAbove is built fresh).

export const WAY_BEHIND_QUEUE_ABOVE_THRESHOLD = 200_000;

export interface FellBehindStepResult {
  nextAbove: Map<string, number>;
  /** Orders that should cue (tone once via caller + card flash). */
  firedOrderIds: string[];
}

/**
 * `wayBehindThreshold` parameterizes ONLY the way-behind branch (#233's
 * per-user delivery threshold). The `0 -> positive` new-level cue is a
 * different signal — "someone got in front of you at a better price" — and is
 * not threshold-scaled, so it survives any threshold the user picks.
 *
 * Defaulted, so every pre-#233 call site keeps today's exact behavior.
 */
export function stepFellBehind(
  prevAbove: ReadonlyMap<string, number>,
  metricsByOrderId: ReadonlyMap<string, { queueAbove: number | null }>,
  wayBehindThreshold: number = WAY_BEHIND_QUEUE_ABOVE_THRESHOLD,
): FellBehindStepResult {
  const nextAbove = new Map<string, number>();
  const firedOrderIds: string[] = [];
  for (const [orderId, metrics] of metricsByOrderId) {
    const above = metrics.queueAbove;
    if (above == null) continue;
    nextAbove.set(orderId, above);
    const last = prevAbove.get(orderId);
    if (last == null) continue; // first sight: silent seed
    const newLevelFormed = last === 0 && above > 0;
    const wentWayBehind = last < wayBehindThreshold && above >= wayBehindThreshold;
    if (newLevelFormed || wentWayBehind) firedOrderIds.push(orderId);
  }
  return { nextAbove, firedOrderIds };
}
